import React, { useMemo, useEffect } from 'react';
import { useQuery, gql } from '@apollo/client';
import { useLocation, Navigate } from 'react-router-dom';
import useFilters from '../hooks/useFilters';
import Product from './Product';
import ProductList from './ProductList';
import Loader from './Loader';
import { useBreadcrumb } from '../context/BreadcrumbContext';

const RESOLVE_URL = gql`
    query ResolveURL($url: String!) {
        route(url: $url) {
            relative_url
            redirect_code
            type
            ... on CategoryTree {
                uid
                id
                name
                breadcrumbs {
                    category_id
                    category_name
                    category_url_path
                }
                __typename
            }
            __typename
        }
    }
`;

const GET_CATEGORY_PRODUCTS = gql`
    query getCategoryProducts(
        $filters: ProductAttributeFilterInput!
        $currentPage: Int = 1
        $pageSize: Int = 20
        $sort: ProductAttributeSortInput
    ) {
        products(
            filter: $filters
            currentPage: $currentPage
            pageSize: $pageSize
            sort: $sort
        ) {
            items {
                id
                uid
                sku
                name
                small_image {
                    url
                }
                url_key
                url_suffix
                price_range {
                    minimum_price {
                        final_price {
                            currency
                            value
                        }
                    }
                }
                rating_summary
                review_count
                stock_status
            }
            total_count
            page_info {
                current_page
                page_size
                total_pages
            }
        }
    }
`;

const Category = ({ enabledLayerNavigation = true }) => {
  const location = useLocation();
  const urlPath = location.pathname; // Keep the leading slash for Magento
  const { updateBreadcrumbs } = useBreadcrumb();

  // Try with .html suffix if not already present
  const urlWithSuffix = urlPath.endsWith('.html') ? urlPath : `${urlPath}.html`;

  // First try: resolve URL as-is
  const { loading: urlLoading1, error: urlError1, data: urlData1 } = useQuery(RESOLVE_URL, {
    variables: { url: urlPath }
  });

  // Second try: resolve URL with .html suffix (only if first query failed or returned null)
  const shouldTryWithSuffix = !urlPath.endsWith('.html') && !urlLoading1 && (!urlData1?.route || !urlData1?.route?.type);
  const { loading: urlLoading2, error: urlError2, data: urlData2 } = useQuery(RESOLVE_URL, {
    variables: { url: urlWithSuffix },
    skip: !shouldTryWithSuffix
  });

  // Use whichever query returned valid data
  const urlData = (urlData1?.route?.type) ? urlData1 : urlData2;
  const urlLoading = urlLoading1 || (shouldTryWithSuffix && urlLoading2);
  const urlError = !urlData?.route?.type ? (urlError2 || urlError1) : null;

  const routeData = urlData?.route;
  const categoryId = routeData?.id;
  const categoryName = routeData?.name || 'Category';

  // Memoize breadcrumbs to prevent infinite loops
  const breadcrumbsData = useMemo(() => {
    // Only set breadcrumbs for category pages, not product pages
    if (!categoryName || routeData?.type !== 'CATEGORY') {
      return null;
    }

    const crumbs = [{ label: 'Home', path: '/' }];

    // Add parent categories from breadcrumbs
    const categoryBreadcrumbs = routeData?.breadcrumbs || [];
    if (categoryBreadcrumbs.length > 0) {
      categoryBreadcrumbs.forEach(crumb => {
        crumbs.push({
          label: crumb.category_name,
          path: `/${crumb.category_url_path}.html`
        });
      });
    }

    // Add current category
    crumbs.push({ label: categoryName, path: urlPath });

    return crumbs;
  }, [categoryId, categoryName, urlPath, routeData?.type]);

  // Update breadcrumbs when data changes (only for categories, not products)
  useEffect(() => {
    if (breadcrumbsData && routeData?.type === 'CATEGORY') {
      updateBreadcrumbs(breadcrumbsData);
    }
  }, [breadcrumbsData, routeData?.type, updateBreadcrumbs]);

  // Use filters hook for filtering functionality
  const {
    activeFilters,
    hasActiveFilters,
    currentPage,
    currentSort,
    isLayerNavigationVisible,
    enabledLayerNavigation: isEnabled,
    addFilter,
    removeFilter,
    clearAllFilters,
    setSort,
    setPage,
    hideLayerNavigation,
    showLayerNavigation,
    setPriceRange,
    graphqlFilters,
    graphqlSort
  } = useFilters(categoryId, enabledLayerNavigation);

  // Prepare variables for GraphQL query
  const queryVariables = useMemo(() => {
    const vars = {
      filters: graphqlFilters,
      currentPage: currentPage,
      pageSize: 20
    };

    if (graphqlSort) {
      vars.sort = graphqlSort;
    }

    return vars;
  }, [graphqlFilters, graphqlSort, currentPage]);

  const { loading: productsLoading, error: productsError, data: productsData } = useQuery(GET_CATEGORY_PRODUCTS, {
    variables: queryVariables,
    skip: !categoryId // Don't run query until we have category ID
  });

  // Handle loading and errors
  if (urlLoading) return <Loader fullScreen />;
  if (urlError) return <div>Error resolving URL: {urlError.message}</div>;

  // Handle redirects
  if (routeData?.redirect_code) {
    return <Navigate to={routeData.relative_url} replace />;
  }

  // Handle product URLs
  if (routeData?.type === 'PRODUCT') {
    // Extract product url_key from the path
    const urlKey = urlPath.substring(urlPath.lastIndexOf('/') + 1).replace('.html', '');
    return <Product urlKey={urlKey} />;
  }

  if (routeData?.type === 'CMS_PAGE') {
    return <div>CMS Page (not implemented)</div>;
  }

  if (!routeData || routeData.type !== 'CATEGORY') {
    return <div>Page not found</div>;
  }

  if (productsLoading) return <Loader fullScreen />;
  if (productsError) return <div>Error: {productsError.message}</div>;

  const products = productsData?.products?.items || [];
  const totalCount = productsData?.products?.total_count || 0;
  const pageInfo = productsData?.products?.page_info;

  return (
    <div className="category-page">
      <h1 className="page-title">{categoryName}</h1>

      <ProductList
        products={products}
        totalCount={totalCount}
        pageInfo={pageInfo}
        currentPage={currentPage}
        onPageChange={setPage}
        currentSort={currentSort}
        onSortChange={setSort}
        enabledLayerNavigation={isEnabled}
        activeFilters={activeFilters}
        onAddFilter={addFilter}
        onRemoveFilter={removeFilter}
        onClearAllFilters={clearAllFilters}
        hasActiveFilters={hasActiveFilters}
        isLayerNavigationVisible={isLayerNavigationVisible}
        onHideLayerNavigation={hideLayerNavigation}
        onShowLayerNavigation={showLayerNavigation}
        onSetPriceRange={setPriceRange}
        categoryId={categoryId}
      />
    </div>
  );
};

export default Category;